import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth.store';
import { useRanchoStore } from '../store/rancho.store';
import { usePermission } from './usePermission';
import { fetchScopedConfig } from '../api/config-scope.api';

export const DEFAULT_TIMEZONE = 'America/Mexico_City';

function isValidTimeZone(tz: unknown): tz is string {
  if (typeof tz !== 'string' || !tz) return false;
  try { new Intl.DateTimeFormat('en-CA', { timeZone: tz }); return true; } catch { return false; }
}

/** Fecha de hoy (YYYY-MM-DD) en la zona dada; zona inválida → America/Mexico_City. */
export function todayInTimezone(tz: string | undefined, now: Date = new Date()): string {
  const timeZone = isValidTimeZone(tz) ? tz : DEFAULT_TIMEZONE;
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

/**
 * Devuelve una función `() => 'YYYY-MM-DD'` con el "hoy" en la zona horaria
 * efectiva (`locale.timezone`) del rancho activo (o del tenant si no hay
 * rancho). Sin permiso `config:read` o mientras carga, usa America/Mexico_City.
 * Es función (no string) para que cada llamada lea la hora actual.
 */
export function useRanchoToday(): () => string {
  const tenantId = useAuthStore(s => s.user?.tenantId);
  const activeRanchoId = useRanchoStore(s => s.activeRanchoId);
  const canReadConfig = usePermission('config:read');

  const scope = activeRanchoId ? 'RANCHO' : 'TENANT';
  const scopeId = activeRanchoId ?? tenantId;

  const { data } = useQuery({
    queryKey: ['scoped-config-tz', scope, scopeId],
    queryFn: () => fetchScopedConfig(scope, scopeId as string),
    enabled: !!scopeId && canReadConfig,
    staleTime: 5 * 60 * 1000,
  });

  const tz = data?.effective?.['locale.timezone']?.value as string | undefined;
  return () => todayInTimezone(tz);
}

/**
 * Fecha "efectiva" que arranca en `today` y se reajusta cuando `today` cambia
 * (p. ej. llega la zona horaria del rancho), salvo que el usuario ya la haya
 * movido; si quedó en el futuro respecto al nuevo "hoy", se recorta.
 * `input` es el valor en vivo; `selected` el debounced que dispara queries.
 */
export function useFollowingToday(today: string, debounceMs = 300) {
  const [selected, setSelected] = useState(today);
  const [input, setInput] = useState(today);
  const userMoved = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (!userMoved.current) {
      setSelected(today);
      setInput(today);
    } else {
      setSelected(d => (d > today ? today : d));
      setInput(d => (d > today ? today : d));
    }
  }, [today]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const change = useCallback((dateStr: string) => {
    userMoved.current = true;
    setInput(dateStr);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setSelected(dateStr), debounceMs);
  }, [debounceMs]);

  return { selected, input, change };
}
